import React, { useState, useEffect } from 'react';
import { ThumbsUp, MinusCircle, ThumbsDown, MessageCircle, Plus, X, Send, CheckCircle, XCircle, Clock, Trash2, EyeOff, Eye, Lock, Globe } from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

const SuggestionBox = ({ user }) => {
    const { t, language } = useI18n(user?.club?.language);
    const dateLocale = language === 'sv' ? 'sv-SE' : language === 'en' ? 'en-GB' : 'fi-FI';
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [newSuggestion, setNewSuggestion] = useState({ title: '', description: '' });
    const [expandedComments, setExpandedComments] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [activeTab, setActiveTab] = useState('Open'); // Open, Implemented, Rejected
    const [boxMode, setBoxMode] = useState('open'); // open, private

    // Fetch box mode setting
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await fetch('/api/settings');
                const data = await res.json();
                const modeSetting = data.find(s => s.key === 'suggestion_box_mode');
                if (modeSetting) {
                    setBoxMode(modeSetting.value);
                }
            } catch (err) {
                console.error('Settings fetch error:', err);
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        fetchSuggestions();
    }, [activeTab]);

    const fetchSuggestions = async () => {
        try {
            const res = await fetch(`/api/suggestions?userId=${user.id}&status=${activeTab}`);
            const data = await res.json();
            setSuggestions(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const deleteSuggestion = async (suggestionId) => {
        if (!window.confirm(t.suggestionBox.confirmDelete)) return;
        try {
            await fetch(`/api/suggestions/${suggestionId}`, { method: 'DELETE' });
            fetchSuggestions();
        } catch (err) {
            console.error(err);
        }
    };

    const toggleHidden = async (suggestionId, isHidden) => {
        try {
            await fetch(`/api/suggestions/${suggestionId}/visibility`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isHidden: !isHidden })
            });
            fetchSuggestions();
        } catch (err) {
            console.error(err);
        }
    };

    const createSuggestion = async () => {
        if (!newSuggestion.title.trim()) return;

        try {
            const res = await fetch('/api/suggestions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: newSuggestion.title,
                    description: newSuggestion.description,
                    authorId: user.id
                })
            });
            if (res.ok) {
                setNewSuggestion({ title: '', description: '' });
                setShowForm(false);
                fetchSuggestions();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const vote = async (suggestionId, voteType) => {
        try {
            await fetch(`/api/suggestions/${suggestionId}/vote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voterId: user.id, voteType })
            });
            fetchSuggestions();
        } catch (err) {
            console.error(err);
        }
    };

    const loadComments = async (suggestionId) => {
        if (expandedComments === suggestionId) {
            setExpandedComments(null);
            return;
        }

        try {
            const res = await fetch(`/api/suggestions/${suggestionId}/comments`);
            const data = await res.json();
            setComments(data);
            setExpandedComments(suggestionId);
            setNewComment('');
        } catch (err) {
            console.error(err);
        }
    };

    const addComment = async (suggestionId) => {
        if (!newComment.trim()) return;

        try {
            await fetch(`/api/suggestions/${suggestionId}/comment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ authorId: user.id, comment: newComment })
            });
            setNewComment('');
            loadComments(suggestionId);
            fetchSuggestions();
        } catch (err) {
            console.error(err);
        }
    };

    const updateStatus = async (suggestionId, status) => {
        try {
            await fetch(`/api/suggestions/${suggestionId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            fetchSuggestions();
        } catch (err) {
            console.error(err);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            Open: { bg: '#3498db', icon: Clock },
            Implemented: { bg: '#27ae60', icon: CheckCircle },
            Rejected: { bg: '#e74c3c', icon: XCircle }
        };
        const config = styles[status] || styles.Open;
        const Icon = config.icon;

        return (
            <span style={{
                background: config.bg,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
            }}>
                <Icon size={12} /> {status === 'Open' ? t.suggestionBox.statusOpen : status === 'Implemented' ? t.suggestionBox.statusImplemented : t.suggestionBox.statusRejected}
            </span>
        );
    };

    // Vote Button Component
    const VoteButton = ({ type, count, voters, isActive, onClick }) => {
        const icons = {
            up: ThumbsUp,
            neutral: MinusCircle,
            down: ThumbsDown
        };
        const colors = {
            up: '#27ae60',
            neutral: '#f39c12',
            down: '#e74c3c'
        };
        const Icon = icons[type];

        return (
            <div style={{ position: 'relative', display: 'inline-block' }} className="vote-btn-wrapper">
                <button
                    onClick={onClick}
                    className="vote-btn"
                    style={{
                        background: isActive ? colors[type] : 'transparent',
                        color: isActive ? 'white' : colors[type],
                        border: `2px solid ${colors[type]}`,
                        borderRadius: '20px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                    }}
                    title={voters.length > 0 ? voters.join(', ') : t.common.noVotes}
                >
                    <Icon size={16} />
                    <span>{count}</span>
                </button>
                {voters.length > 0 && (
                    <div className="vote-tooltip">
                        {voters.join(', ')}
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <div className="loading">{t.common.loading}</div>;

    return (
        <div className="suggestion-box">
            {/* Hero Section */}
            <div className="card" style={{ 
                background: 'linear-gradient(135deg, #8b4513 0%, #d35400 100%)', 
                color: 'white',
                textAlign: 'center',
                marginBottom: '1.5rem'
            }}>
                <h1 style={{ marginBottom: '0.5rem' }}>{t.suggestionBox.title}</h1>
                <p style={{ opacity: 0.9 }}>{t.suggestionBox.subtitle}</p>
            </div>

            {/* Mode indicator for private mode */}
            {boxMode === 'private' && (
                <div style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#f59e0b'
                }}>
                    <Lock size={16} />
                    <span>{t.suggestionBox.privateMode}</span>
                </div>
            )}

            {/* Tab Filters */}
            <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginBottom: '1rem',
                flexWrap: 'wrap'
            }}>
                {['Open', 'Implemented', 'Rejected'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => { setActiveTab(tab); setLoading(true); }}
                        style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            border: activeTab === tab ? 'none' : '2px solid #374151',
                            background: activeTab === tab 
                                ? (tab === 'Open' ? '#3498db' : tab === 'Implemented' ? '#27ae60' : '#e74c3c')
                                : 'transparent',
                            color: activeTab === tab ? 'white' : '#9ca3af',
                            cursor: 'pointer',
                            fontWeight: activeTab === tab ? 600 : 400,
                            transition: 'all 0.2s'
                        }}
                    >
                        {tab === 'Open' && <Clock size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />}
                        {tab === 'Implemented' && <CheckCircle size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />}
                        {tab === 'Rejected' && <XCircle size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />}
                        {tab === 'Open' ? t.suggestionBox.tabOpen : tab === 'Implemented' ? t.suggestionBox.tabImplemented : t.suggestionBox.tabRejected}
                    </button>
                ))}
            </div>

            {/* Create Button */}
            <button 
                className="btn-primary" 
                onClick={() => setShowForm(true)}
                style={{ marginBottom: '1.5rem' }}
            >
                <Plus size={18} /> {t.suggestionBox.suggestNew}
            </button>

            {/* Create Form Modal */}
            {showForm && (
                <div className="modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="modal-content card" onClick={e => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setShowForm(false)}><X /></button>
                        <h3>{t.suggestionBox.newSuggestion}</h3>
                        
                        <div className="form-group">
                            <label>{t.suggestionBox.titleLabel}</label>
                            <input
                                type="text"
                                placeholder={t.suggestionBox.titlePlaceholder}
                                value={newSuggestion.title}
                                onChange={e => setNewSuggestion({ ...newSuggestion, title: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>{t.suggestionBox.descriptionLabel}</label>
                            <textarea
                                placeholder={t.suggestionBox.descriptionPlaceholder}
                                rows={4}
                                value={newSuggestion.description}
                                onChange={e => setNewSuggestion({ ...newSuggestion, description: e.target.value })}
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        <button className="btn-primary" onClick={createSuggestion}>
                            {t.suggestionBox.submit}
                        </button>
                    </div>
                </div>
            )}

            {/* Suggestions List */}
            <div className="suggestions-list">
                {suggestions.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', color: '#666' }}>
                        <p>{t.suggestionBox.noSuggestions}</p>
                    </div>
                ) : (
                    suggestions.map(s => (
                        <div key={s.id} className="card suggestion-card" style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                <div>
                                    <h3 style={{ marginBottom: '0.25rem' }}>{s.title}</h3>
                                    <small style={{ color: '#666' }}>
                                        {s.author.name} • {new Date(s.createdAt).toLocaleDateString(dateLocale)}
                                    </small>
                                </div>
                                {getStatusBadge(s.status)}
                            </div>

                            {s.description && (
                                <p style={{ margin: '0.75rem 0', color: '#444' }}>{s.description}</p>
                            )}

                            {/* Voting Section */}
                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                <VoteButton
                                    type="up"
                                    count={s.votes.up.count}
                                    voters={s.votes.up.voters}
                                    isActive={s.userVote === 1}
                                    onClick={() => vote(s.id, 1)}
                                />
                                <VoteButton
                                    type="neutral"
                                    count={s.votes.neutral.count}
                                    voters={s.votes.neutral.voters}
                                    isActive={s.userVote === 0}
                                    onClick={() => vote(s.id, 0)}
                                />
                                <VoteButton
                                    type="down"
                                    count={s.votes.down.count}
                                    voters={s.votes.down.voters}
                                    isActive={s.userVote === -1}
                                    onClick={() => vote(s.id, -1)}
                                />

                                <button
                                    onClick={() => loadComments(s.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#666',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        marginLeft: 'auto'
                                    }}
                                >
                                    <MessageCircle size={16} />
                                    {s.commentsCount > 0 ? formatMemberText(t.suggestionBox.comments, { count: s.commentsCount }) : t.suggestionBox.comment}
                                </button>
                            </div>

                            {/* Admin Controls */}
                            {user.role === 'admin' && (
                                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    {s.status === 'Open' && (
                                        <>
                                            <button
                                                className="btn-secondary"
                                                onClick={() => updateStatus(s.id, 'Implemented')}
                                                style={{ background: '#27ae60', color: 'white' }}
                                            >
                                                <CheckCircle size={14} /> {t.suggestionBox.markImplemented}
                                            </button>
                                            <button
                                                className="btn-secondary"
                                                onClick={() => updateStatus(s.id, 'Rejected')}
                                                style={{ background: '#e74c3c', color: 'white' }}
                                            >
                                                <XCircle size={14} /> {t.suggestionBox.markRejected}
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="btn-secondary"
                                        onClick={() => toggleHidden(s.id, s.isHidden)}
                                        style={{ 
                                            background: s.isHidden ? 'rgba(245, 158, 11, 0.2)' : 'transparent',
                                            border: '1px solid #f59e0b',
                                            color: '#f59e0b'
                                        }}
                                    >
                                        {s.isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                                        {s.isHidden ? ` ${t.suggestionBox.show}` : ` ${t.suggestionBox.hide}`}
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => deleteSuggestion(s.id)}
                                        style={{ 
                                            background: 'transparent',
                                            border: '1px solid #ef4444',
                                            color: '#ef4444'
                                        }}
                                    >
                                        <Trash2 size={14} /> {t.common.delete}
                                    </button>
                                </div>
                            )}
                            
                            {/* Hidden indicator */}
                            {s.isHidden && user.role === 'admin' && (
                                <div style={{
                                    marginTop: '0.5rem',
                                    fontSize: '0.75rem',
                                    color: '#f59e0b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                }}>
                                    <EyeOff size={12} /> {t.suggestionBox.hiddenFromUsers}
                                </div>
                            )}

                            {/* Comments Section */}
                            {expandedComments === s.id && (
                                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                                    <h4 style={{ marginBottom: '0.75rem' }}>{t.suggestionBox.commentsTitle}</h4>
                                    
                                    {comments.length === 0 ? (
                                        <p style={{ color: '#666', fontStyle: 'italic' }}>{t.suggestionBox.noComments}</p>
                                    ) : (
                                        <div style={{ marginBottom: '1rem' }}>
                                            {comments.map(c => (
                                                <div key={c.id} style={{ 
                                                    background: '#f9f9f9', 
                                                    padding: '0.75rem', 
                                                    borderRadius: '4px',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                        <strong style={{ fontSize: '0.9rem' }}>{c.author.name}</strong>
                                                        <small style={{ color: '#888' }}>
                                                            {new Date(c.createdAt).toLocaleString(dateLocale)}
                                                        </small>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '0.95rem' }}>{c.comment}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <input
                                            type="text"
                                            placeholder={t.suggestionBox.addComment}
                                            value={newComment}
                                            onChange={e => setNewComment(e.target.value)}
                                            onKeyPress={e => e.key === 'Enter' && addComment(s.id)}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            className="btn-primary"
                                            onClick={() => addComment(s.id)}
                                            style={{ width: 'auto', padding: '0.5rem 1rem' }}
                                        >
                                            <Send size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SuggestionBox;

